import React from "react";
import { AuthContext } from "./AuthContextCore";
import { AuthContextType, AuthProviderProps } from "./types";
import useAuthState from "./useAuthState";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import useIdleLogout from "@/hooks/useIdleTimeout";
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Use the hook inside the functional component
  const {
    currentUser,
    loading,
    session,
    error: authStateError,
  } = useAuthState();
  const { toast } = useToast();

  React.useEffect(() => {
    if (authStateError) {
      toast({
        title: "Authentication Error",
        description: authStateError,
        variant: "destructive",
      });
    }
  }, [authStateError, toast]);

  // Determine if user is authenticated AND verified
  const isAuthenticated =
    !!currentUser && !!session && !!currentUser.email_confirmed_at;

  // Set up idle logout for authenticated users
  useIdleLogout(24 * 60 * 60 * 1000, isAuthenticated);

  // Check if user is admin - based on user metadata or email domain
  // For this example, we'll use a simple check with specific admin emails
  const adminEmails = [
    "admin@proptpilot.com",
    "support@proptpilot.com",
    "simi.taiwo@goshenhomesandproperties.com",
  ];

  // Make email comparison case-insensitive to prevent issues
  const userEmail = currentUser?.email?.toLowerCase() || "";
  const isAdmin = adminEmails.some(
    (email) => email.toLowerCase() === userEmail
  );

  console.log("AuthProvider - User Email:", userEmail);
  console.log("AuthProvider - Is Admin Check Result:", isAdmin);
  console.log(
    "AuthProvider - Email Confirmed:",
    !!currentUser?.email_confirmed_at
  );

  // Set up login function
  const login = async (email: string, password: string) => {
    try {
      console.log("AuthProvider - Starting login for:", email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Session tracking removed - Supabase handles session management internally
      // The user_sessions table validation was causing login issues

      // STRICT CHECK: Block any user without email verification
      if (data.user && !data.user.email_confirmed_at) {
        // Immediately sign out any unverified user
        await supabase.auth.signOut();

        toast({
          title: "Email verification required",
          description:
            "You must verify your email address before you can sign in. Please check your email for a verification link.",
          variant: "destructive",
        });

        return {
          success: false,
          error:
            "Email verification required. Please check your email and click the verification link before signing in.",
          needsVerification: true,
          userEmail: data.user.email,
        };
      }

      // Only allow fully verified users to proceed
      if (data?.user && data?.session && data.user.email_confirmed_at) {
        console.log(
          "AuthProvider - Login successful, preparing welcome message"
        );

        // Show welcome message with user's name or email
        const userName =
          data.user.user_metadata?.name ||
          data.user.email?.split("@")[0] ||
          "there";

        // Use setTimeout to ensure the toast appears after navigation
        setTimeout(() => {
          console.log("AuthProvider - Showing welcome toast for:", userName);
          toast({
            title: `Welcome back, ${userName}! 👋`,
            description: "You've successfully signed in to ProptPilot.",
            duration: 4000,
          });
        }, 500); // Increased delay to ensure it shows after page navigation

        return { success: true };
      } else {
        return {
          success: false,
          error: "Unable to authenticate. Please try again.",
        };
      }
    } catch (error: any) {
      console.error("AuthProvider - Login error:", error);

      // Provide more user-friendly error messages
      let errorMessage = "An error occurred while logging in.";

      if (error.message?.includes("Invalid login credentials")) {
        errorMessage = "Invalid email or password. Please try again.";
      } else if (
        error.message?.includes("Email not confirmed") ||
        error.message?.includes("email must be confirmed")
      ) {
        errorMessage = "Please verify your email before logging in.";
      }

      toast({
        title: "Login failed",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  // Set up logout function
  const logout = async () => {
    try {
      // Session invalidation handled by Supabase auth.signOut()

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error: any) {
      toast({
        title: "Logout failed",
        description: "An error occurred while logging out.",
        variant: "destructive",
      });
    }
  };

  // Set up signup function with country metadata
  const signup = async (email: string, password: string, metadata?: object) => {
    try {
      console.log("Attempting signup with:", email, "Country:", metadata);

      // Use Supabase's built-in signup with email verification
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login?verified=true`,
          data: metadata,
        },
      });

      if (error) throw error;

      // Mark this browser so the welcome video plays once after first login
      try {
      } catch {
        // storage unavailable — skip welcome video
      }

      // Check if user was created but needs email confirmation
      if (data.user && !data.session) {
        // User created but not confirmed - show verification message

        toast({
          title: "Account created successfully",
          description:
            "Please check your email and click the verification link to complete your registration.",
        });

        console.log("User created, waiting for email verification");
        return { success: true };
      } else if (data.user && data.session) {
        // User created and immediately logged in (email confirmation disabled)
        console.log("User created and logged in immediately");

        // Show welcome message for new users
        const userName =
          data.user.user_metadata?.name ||
          data.user.email?.split("@")[0] ||
          "there";

        // Use setTimeout to ensure the toast appears properly
        setTimeout(() => {
          toast({
            title: `Welcome to ProptPilot, ${userName}! 🎉`,
            description:
              "Your account has been created successfully. Let's get started!",
            duration: 5000,
          });
        }, 500);

        return { success: true };
      } else {
        return {
          success: false,
          error: "Signup failed. Please try again later.",
        };
      }
    } catch (error: any) {
      // Provide more user-friendly error messages
      let errorMessage = error.message || "An error occurred while signing up.";

      if (error.message?.includes("User already registered")) {
        errorMessage =
          "This email is already registered. Try logging in instead.";
      }

      toast({
        title: "Sign up failed",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  // Combine all values for the context
  const authContextValue: AuthContextType = {
    isAuthenticated,
    currentUser,
    loading,
    isAdmin,
    supabaseClient: supabase, // Add the supabaseClient to the context
    login,
    logout,
    signup,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
